import { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqCategories, faqItems } from '../../mocks/faq';

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState('booking');
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleItem = (id: number) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const filteredFaqs = faqItems.filter((item) => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesSearch =
      searchQuery === '' ||
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png"
                alt="봉구 투어 로고"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-gray-900">봉구 투어</span>
            </Link>
            <div className="flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                홈
              </Link>
              <Link to="/#tours" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                투어 상품
              </Link>
              <Link to="/#reviews" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                고객 후기
              </Link>
              <Link to="/faq" className="text-sm font-medium text-[#00BCD4] cursor-pointer whitespace-nowrap">
                자주 묻는 질문
              </Link>
              <Link to="/#contact" className="px-6 py-2.5 bg-[#1A237E] text-white text-sm font-semibold rounded-full hover:bg-[#0D47A1] transition-colors cursor-pointer whitespace-nowrap">
                문의하기
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#00BCD4] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-[#FF6B6B] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0F7FA] rounded-full mb-6">
            <i className="ri-customer-service-2-line text-[#00BCD4]"></i>
            <span className="text-sm font-semibold text-[#00BCD4]">고객 지원</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4 leading-tight">
            자주 묻는 질문
          </h1>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            봉구 투어 이용에 대해 궁금하신 점을 빠르게 확인하세요.<br />
            원하는 답변을 찾지 못하셨다면 언제든 문의해 주세요.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder="궁금한 내용을 검색해보세요"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-14 pr-6 py-4 bg-white rounded-2xl shadow-lg border border-gray-100 focus:outline-none focus:border-[#00BCD4] text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="px-6 pb-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {faqCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setOpenItems([]); }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.id
                    ? 'bg-[#00BCD4] text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 text-lg mb-2">검색 결과가 없습니다</p>
              <p className="text-gray-400 text-sm">다른 키워드로 검색하거나 카테고리를 변경해 보세요</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((item) => (
                <div
                  key={item.id}
                  className={`bg-white rounded-2xl border transition-all duration-300 ${
                    openItems.includes(item.id)
                      ? 'border-[#00BCD4]/30 shadow-lg shadow-[#00BCD4]/5'
                      : 'border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5'
                  }`}
                >
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                          openItems.includes(item.id) ? 'bg-[#00BCD4] text-white' : 'bg-[#E0F7FA] text-[#00BCD4]'
                        }`}
                      >
                        <i className="ri-question-line text-base"></i>
                      </div>
                      <span
                        className={`font-semibold text-[15px] transition-colors duration-300 ${
                          openItems.includes(item.id) ? 'text-[#00BCD4]' : 'text-gray-900'
                        }`}
                      >
                        {item.question}
                      </span>
                    </div>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ml-4 transition-all duration-300 ${
                        openItems.includes(item.id)
                          ? 'bg-[#00BCD4] text-white rotate-180'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-lg"></i>
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openItems.includes(item.id) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-7 pb-6 pl-19">
                      <div className="ml-12 pl-0 border-l-2 border-[#00BCD4]/20 pl-5">
                        <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/496c640c117a1fb604afd392fc23fdfa.jpg"
              alt="세부 바다"
              className="w-full h-[280px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A237E]/85 via-[#1A237E]/70 to-[#00BCD4]/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <h3 className="text-3xl font-bold mb-3">원하는 답변을 찾지 못하셨나요?</h3>
                <p className="text-white/80 mb-8 text-base">전문 상담사가 직접 도와드립니다</p>
                <div className="flex items-center justify-center gap-4">
                  <Link
                    to="/#contact"
                    className="px-7 py-3 bg-white text-[#1A237E] font-semibold rounded-full hover:bg-gray-100 transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line"></i>
                    1:1 문의하기
                  </Link>
                  <a
                    href="tel:+639171234567"
                    className="px-7 py-3 bg-[#00BCD4] text-white font-semibold rounded-full hover:bg-[#00ACC1] transition-colors inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    전화 상담
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl p-12">
            <div className="grid grid-cols-5 gap-12 mb-12">
              <div className="col-span-1">
                <Link to="/" className="flex items-center gap-2 mb-4 cursor-pointer">
                  <img
                    src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png"
                    alt="봉구 투어"
                    className="h-10 w-auto"
                  />
                  <span className="text-xl font-bold text-gray-900">봉구 투어</span>
                </Link>
                <p className="text-sm text-gray-600 leading-relaxed">
                  필리핀 세부 현지 여행 전문<br />안전한 투어 보장
                </p>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4"><a href="/#tours">TOURS</a></h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/tour/oslob-whale-shark" className="hover:text-[#00BCD4] transition-colors cursor-pointer">고래상어 투어</Link></li>
                  <li><Link to="/tour/kawasan-falls" className="hover:text-[#00BCD4] transition-colors cursor-pointer">카와산 폭포</Link></li>
                  <li><Link to="/tour/bohol-day-tour" className="hover:text-[#00BCD4] transition-colors cursor-pointer">보홀 당일 투어</Link></li>
                  <li><Link to="/tour/sunset-cruise" className="hover:text-[#00BCD4] transition-colors cursor-pointer">선셋 크루즈</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">COMPANY</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/about" className="hover:text-[#00BCD4] transition-colors cursor-pointer">회사 소개</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">가이드 소개</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">파트너십</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">채용 정보</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">SUPPORT</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/faq" className="text-[#00BCD4] font-medium cursor-pointer">자주 묻는 질문</Link></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">예약 문의</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">환불 정책</a></li>
                  <li><a href="#" className="hover:text-[#00BCD4] transition-colors cursor-pointer">이용 약관</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-4">FOLLOW US</h4>
                <div className="flex gap-3">
                  <a href="#" className="w-10 h-10 bg-[#FFE500] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-kakao-talk-fill text-[#3C1E1E] text-xl"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#515BD4] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-instagram-line text-white text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-8 flex items-center justify-between">
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <span>&copy; 2025 봉구 투어. All rights reserved.</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#00BCD4] transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
